import React, { useState } from "react";
import { ArrowRight2, Flag, Message, Star1, UserRemove } from "iconsax-reactjs";
import { useAdminOverview } from "../hooks/useAdminOverview";
import { DayColumns, DayTable, OverviewToolbar, StatStrip, StatTile, dayLabel } from "./overview";
import { PERIODS, changeOf, formatCount, formatSigned } from "../utils/overviewData";
import "./AdminOverview.css";

const AUDIENCES = {
  "All Registered Users": "All users",
  "Tenants Only": "Tenants",
  "Dealers Only": "Dealers",
};

const plural = (n, one, many) => `${formatCount(n)} ${n === 1 ? one : many}`;
const last14 = (series) => series.slice(-14).map((day) => day.count);

function sentAgo(ms) {
  if (!ms) return "";
  const minutes = Math.floor((Date.now() - ms) / 60000);
  if (minutes < 60) return minutes < 1 ? "just now" : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/** The GharSetu team's Dashboard: the marketplace at a glance and the queues waiting on the team. */
export default function AdminOverview({ setActiveTab, supportUnread = 0, supportWaiting = 0, reportsOpen = 0 }) {
  const [periodId, setPeriodId] = useState("7d");
  const [chartView, setChartView] = useState("chart");
  const {
    status, refreshing, updatedAt, refresh, win, listings, signups, leads, queue, reels, push, errors,
  } = useAdminOverview(periodId);

  const period = PERIODS.find((p) => p.id === periodId) || PERIODS[0];
  const loading = status !== "ready";
  const dim = refreshing ? " ov-dim" : "";
  const compareText = period.before;
  const leadsCounting = leads.state === "loading";
  const leadsReady = leads.state === "ready" || leads.state === "partial";

  const busiest = listings?.added.series.reduce((best, day) => (day.count > (best?.count || 0) ? day : best), null);

  const queueRows = [
    {
      id: "chat",
      icon: Message,
      label: "Support chats",
      detail: plural(supportUnread, "unread message", "unread messages"),
      count: supportWaiting,
      alert: true,
    },
    {
      id: "reports",
      icon: Flag,
      label: "Open reports",
      detail: queue ? `${formatCount(queue.reports.current)} filed this period` : "",
      count: reportsOpen,
    },
    {
      id: "reviews",
      icon: Star1,
      label: "Reviews to approve",
      detail: "Waiting before they show in the app",
      count: queue?.pendingReviews ?? null,
    },
    {
      id: "users",
      icon: UserRemove,
      label: "Accounts pending deletion",
      detail: "Removed after the waiting period",
      count: queue?.pendingDeletions ?? null,
    },
  ];

  return (
    <div className="ov-page ao-page">
      <OverviewToolbar
        periodId={periodId}
        onPeriodChange={setPeriodId}
        compareText={`Compared with ${period.before}`}
        updatedAt={updatedAt}
        onRefresh={refresh}
        refreshing={refreshing}
      />

      <StatStrip label="Marketplace numbers">
        <StatTile
          label="Live listings"
          value={errors.listings ? "Not loaded" : listings?.live ?? 0}
          loading={loading}
          note={listings ? `${formatCount(listings.hidden)} hidden, ${formatCount(listings.total)} in all` : errors.listings ? "Couldn't load" : undefined}
          linkLabel="Open Properties"
          onOpen={() => setActiveTab("properties")}
        />
        <StatTile
          label="Listings added"
          value={errors.listings ? "Not loaded" : listings?.added.current ?? 0}
          loading={loading}
          change={listings ? changeOf(listings.added.current, listings.added.previous) : null}
          compareText={compareText}
          trend={listings ? last14(listings.added.series) : null}
          trendLabel="Listings added per day, last 14 days"
          linkLabel="Open Properties"
          onOpen={() => setActiveTab("properties")}
        />
        <StatTile
          label="Leads across dealers"
          value={leads.state === "error" ? "Not loaded" : leads.current ?? 0}
          loading={leadsCounting}
          change={leadsReady ? changeOf(leads.current, leads.previous) : null}
          compareText={compareText}
          note={
            leadsCounting ? "Counting across dealers"
              : leads.state === "error" ? "Couldn't load the dealer list"
                : `${formatCount(leads.direct)} direct, ${formatCount(leads.nearby)} nearby${leads.failed ? `; ${plural(leads.failed, "dealer", "dealers")} couldn't be counted` : ""}`
          }
          linkLabel="Open Dealers"
          onOpen={() => setActiveTab("dealers")}
        />
        <StatTile
          label="Sign-ups"
          value={errors.signups ? "Not loaded" : signups?.current ?? 0}
          loading={loading}
          change={signups ? changeOf(signups.current, signups.previous) : null}
          compareText={compareText}
          trend={signups ? last14(signups.series) : null}
          trendLabel="Sign-ups per day, last 14 days"
          note={signups ? `${formatCount(signups.tenants)} tenants, ${formatCount(signups.dealers)} dealers in all` : undefined}
          linkLabel="Open Users"
          onOpen={() => setActiveTab("users")}
        />
      </StatStrip>

      <div className="ov-row">
        <section className={`card ov-card${dim}`} aria-labelledby="ao-trend-title">
          <div className="card-header">
            <h2 className="card-title" id="ao-trend-title">Listings added and sign-ups</h2>
            <div className="ov-head-actions">
              {busiest && <span className="ov-card-meta">Busiest day: {dayLabel(busiest.start)}</span>}
              <div className="segmented" role="group" aria-label="Show as">
                <button type="button" aria-pressed={chartView === "chart"} onClick={() => setChartView("chart")}>Chart</button>
                <button type="button" aria-pressed={chartView === "table"} onClick={() => setChartView("table")}>Table</button>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="skeleton ao-skel-chart" />
            ) : !listings && !signups ? (
              <p className="ov-error">Couldn't load listings or sign-ups. Refresh to try again.</p>
            ) : chartView === "table" ? (
              <DayTable
                series={(listings || signups).added?.series || (listings || signups).series}
                caption="Listings added and sign-ups per day"
                columns={[
                  ...(listings ? [{ label: "Listings added", counts: listings.added.series.map((day) => day.count) }] : []),
                  ...(signups ? [{ label: "Sign-ups", counts: signups.series.map((day) => day.count) }] : []),
                ]}
              />
            ) : (
              <div className="ov-charts">
                {listings ? (
                  <DayColumns
                    series={listings.added.series}
                    label="Listings added"
                    unit={(n) => plural(n, "listing", "listings")}
                    showDates={!signups}
                  />
                ) : (
                  <p className="ov-error">Listings couldn't be loaded.</p>
                )}
                {signups ? (
                  <DayColumns
                    series={signups.series}
                    label="Sign-ups"
                    unit={(n) => plural(n, "sign-up", "sign-ups")}
                    showDates
                  />
                ) : (
                  <p className="ov-error">Sign-ups couldn't be loaded.</p>
                )}
                {win.series.length > win.days && <p className="ov-key">Lighter columns are {period.before}.</p>}
              </div>
            )}
            <p className="ov-footnote ao-footnote">
              Sign-ups count accounts with a sign-up date; some older accounts have none.
            </p>
          </div>
        </section>

        <section className="card ov-card" aria-labelledby="ao-queue-title">
          <div className="card-header">
            <h2 className="card-title" id="ao-queue-title">Needs attention</h2>
          </div>
          <ul className="ao-queue">
            {queueRows.map((row) => {
              const Icon = row.icon;
              const known = row.count !== null && row.count !== undefined;
              return (
                <li key={row.id}>
                  <button type="button" className="ao-queue-row" onClick={() => setActiveTab(row.id)}>
                    <span className="ao-queue-icon" aria-hidden="true">
                      <Icon size={18} variant="Linear" />
                    </span>
                    <span className="ao-queue-text">
                      <span className="ao-queue-label">{row.label}</span>
                      {row.detail && <span className="ao-queue-detail">{row.detail}</span>}
                    </span>
                    {!known ? (
                      <span className="ao-queue-clear">{loading ? "…" : "Not loaded"}</span>
                    ) : row.count === 0 ? (
                      <span className="ao-queue-clear">Clear</span>
                    ) : row.alert ? (
                      <span className="count-badge alert">{formatCount(row.count)}</span>
                    ) : (
                      <span className="ao-queue-count">{formatCount(row.count)}</span>
                    )}
                    <ArrowRight2 size={14} variant="Linear" className="ao-queue-go" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <div className="ov-row">
        <section className={`card ov-card${leadsCounting ? "" : dim}`} aria-labelledby="ao-dealers-title">
          <div className="card-header">
            <h2 className="card-title" id="ao-dealers-title">Busiest dealers this period</h2>
            <button type="button" className="ov-link ov-head-actions" onClick={() => setActiveTab("dealers")}>
              Open Dealers
              <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
            </button>
          </div>
          {leadsCounting ? (
            <div className="card-body"><div className="skeleton ao-skel-list" /></div>
          ) : leads.state === "error" ? (
            <p className="ao-empty">Couldn't load the dealer list. Refresh to try again.</p>
          ) : leads.busiest.length === 0 ? (
            <p className="ao-empty">No leads came in this period.</p>
          ) : (
            <div className="ao-table-wrap">
              <table className="table ao-dealers">
                <thead>
                  <tr>
                    <th scope="col">Dealer</th>
                    <th scope="col" className="num">Direct leads</th>
                    <th scope="col" className="num">Nearby leads</th>
                    <th scope="col" className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.busiest.map((dealer) => (
                    <tr key={dealer.uid}>
                      <td>
                        <span className="ao-dealer">
                          <span className="avatar" aria-hidden="true">{dealer.name.charAt(0).toUpperCase()}</span>
                          <span className="ao-dealer-name">{dealer.name}</span>
                        </span>
                      </td>
                      <td className="num">{formatCount(dealer.direct)}</td>
                      <td className="num">{formatCount(dealer.nearby)}</td>
                      <td className="num ao-total">{formatCount(dealer.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {leads.state === "partial" && (
            <p className="ov-footnote ao-partial">
              {plural(leads.failed, "dealer", "dealers")} couldn't be counted, so these totals may be low.
            </p>
          )}
        </section>

        <section className={`card ov-card${dim}`} aria-labelledby="ao-now-title">
          <div className="card-header">
            <h2 className="card-title" id="ao-now-title">Marketplace now</h2>
          </div>
          {loading ? (
            <div className="card-body"><div className="skeleton ao-skel-list" /></div>
          ) : (
            <dl className="ao-facts">
              <div>
                <dt>Tenants</dt>
                <dd>{signups ? formatCount(signups.tenants) : "Not loaded"}</dd>
              </div>
              <div>
                <dt>Dealers</dt>
                <dd>{signups ? formatCount(signups.dealers) : "Not loaded"}</dd>
              </div>
              <div>
                <dt>Live reels</dt>
                <dd>
                  {reels ? formatCount(reels.live) : "Not loaded"}
                  {reels && <span className="ao-fact-note">{formatCount(reels.views)} views all time</span>}
                </dd>
              </div>
              <div>
                <dt>Reels published</dt>
                <dd>
                  {reels ? formatCount(reels.published.current) : "Not loaded"}
                  {reels && (
                    <span className="ao-fact-note">
                      {formatSigned(reels.published.current - reels.published.previous)} vs {compareText}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Push-ready devices</dt>
                <dd>{push ? formatCount(push.devices) : "Not loaded"}</dd>
              </div>
              <div>
                <dt>Last broadcast</dt>
                <dd>
                  {push?.lastBroadcast ? (
                    <>
                      <span className="ao-broadcast">{push.lastBroadcast.title}</span>
                      <span className="ao-fact-note">
                        {sentAgo(push.lastBroadcast.sentAt)}, to {(AUDIENCES[push.lastBroadcast.targetSegment] || "All users").toLowerCase()}
                      </span>
                    </>
                  ) : push ? "None sent yet" : "Not loaded"}
                </dd>
              </div>
            </dl>
          )}
          <div className="ao-now-foot">
            <button type="button" className="ov-link" onClick={() => setActiveTab("notifications")}>
              Open Notifications
              <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
